
import { computed, defineComponent, reactive, ref, watch } from 'vue';
import {
  DEMAND_TASK_MACHINE_LIST,
  REGION_LIST,
} from '@/constant/production-demand';
import MachineOrder from './components/machine-order.vue';

import { ElMessage, ElMessageBox } from 'element-plus';
import { useRoute, useRouter } from 'vue-router';
import { IFile, IFileData } from '@/components/uploader/packages/types';
import usePermissionConfig from './use-permission-config';
import {
  getCustomerCreditInfo,
  getProductionDemandDetail,
  updateProductionDemand,
} from '@/api/production-demand';
import { useDictionary } from '@/hooks/use-dict';
import { useOpsCascader, useClothesSize } from '@/hooks/use-cascader';
import {
  IV1ProdDemandRes,
  IV1ProdDemandClothingStyleColorItem,
  ICssCustomerInfoRes,
} from '@/api/production-demand/type';

export interface IMachineData extends IV1ProdDemandRes {
  _styleType: string[];
  styleColor: string[];
  _stylePictureList: IFile[];
  designFileUrlList: IFile[];
}

export default defineComponent({
  components: {
    MachineOrder,
  },
  setup() {
    const { params } = useRoute();
    const demandDetailId = params.id as string;

    const formData = reactive({
      demandDetailId,
      regionId: '',
      regionName: '',
      demandTaskType: '',
      serialNumber: '',
      images: [] as string[],
      _images: [] as IFile[],
    });

    // 加工
    const machineFormData = ref({
      serviceObject: '',
      serviceObjectId: '',
      oldPrototypeCode: '',
      purchaserId: '',
      purchaserName: '',
      purchaserCode: '',
      purchaserContactId: '',
      customerStyleCode: '',
      styleType: '',
      styleTypeName: '', // 款式品类 名 的 字符串
      _styleType: [] as string[], // 款式品类的 字符串
      bdName: '',
      bdId: '',
      bdCode: '',
      clothingStyleSeason: [] as string[],
      designerId: '',
      clothingSizeType: '',
      clothingSizeTypeName: '', // 名字
      clothingSizeLowerLimit: '',
      clothingSizeUpperLimit: '',
      styleColor: [] as string[],
      clothingStyleColor: [] as IV1ProdDemandClothingStyleColorItem[],
      qualityLevel: '',
      // 字符串[]
      stylePictureList: [] as string[],
      _stylePictureList: [] as IFileData[],
      productionNum: '',
      productionUnit: '',
      purchaserExpectQuoPrice: '',
      requireDeliveryTimeLowerLimit: '',
      requireDeliveryTimeUpperLimit: '',
      productionPattern: '',
      pickupFabricWay: '',
      shippingAddress: {
        postProvince: '',
        postCity: '',
        postArea: '',
        postAddressDetail: '',
      },
      designFileUrlList: [] as IFileData[],
      designFileUrl: '',
      designFileName: '',
      remark: '',
    } as IMachineData);

    // 授信查询
    const creditData = ref({} as ICssCustomerInfoRes);
    watch(() => machineFormData.value.purchaserCode, async (val) => {
      if (val) {
        try {
          const { data } = await getCustomerCreditInfo({ customerCode: val });
          creditData.value = data;
          const creditStatus = !!creditData.value?.ccsCustomer;
          !creditStatus && ElMessage.warning('该客户未完成授信，请联系BD在CRM系统完成授信审核！');
        } catch (err) {
          console.log(err);
          creditData.value = {} as ICssCustomerInfoRes;
        }
      }
    });
    // 是否授信客户，同时满足customerStatus=1，amountStatus=1
    const isCreditCustomer = computed(() => {
      return !!creditData.value?.ccsCustomer;
    });

    // 初始化
    const init = async () => {
      // 加工订单 - 生产需求详情
      const { data } = await getProductionDemandDetail(demandDetailId);
      const { regionId, demandTaskType, demandDetailId: demandId } = data;
      formData.regionId = regionId;
      formData.demandTaskType = demandTaskType;
      formData.demandDetailId = demandId!;

      machineFormData.value = data as IMachineData;
      // 款式图片
      machineFormData.value._stylePictureList = data.stylePictureList.map((item: string) => {
        return { url: item, name: item };
      });

      // 款式品类
      machineFormData.value._styleType = machineFormData.value.styleType?.split('-') ?? [];
      // 款式颜色
      machineFormData.value.clothingStyleColor = data.clothingStyleColor;
      machineFormData.value.styleColor = data.clothingStyleColor
        .map((it: IV1ProdDemandClothingStyleColorItem) => it.color);
      // 处理收货地址
      if (!machineFormData.value.shippingAddress) {
        machineFormData.value.shippingAddress = {
          postProvince: '',
          postCity: '',
          postArea: '',
          postAddressDetail: '',
        };
      }
    };
    init();

    // 主表单
    const mainFormRef = ref();
    // 加工
    const machineRef = ref();

    // 区域 名
    watch(() => formData.regionId, (val) => {
      const item = REGION_LIST.find(it => it.value === val);
      formData.regionName = item?.label ?? '';
    });
    // 审批截图
    watch(() => formData._images, (list) => {
      formData.images = list?.map(item => item.url) ?? [];
    });

    // 路由
    const router = useRouter();

    // 提交
    const handleSubmit = async () => {
      try {
        // 校验
        await mainFormRef.value?.validate();
        // 加工订单校验
        await machineRef.value?.validateForm();

        const data = {
          demandDetailId,
          regionId: formData.regionId,
          purchaserId: machineFormData.value.purchaserId,
          purchaserContactId: machineFormData.value.purchaserContactId,
          bdId: machineFormData.value.bdId,
          designerId: machineFormData.value.designerId,
          customerStyleCode: machineFormData.value.customerStyleCode,
          oldPrototypeCode: machineFormData.value.oldPrototypeCode,
          productionPattern: machineFormData.value.productionPattern,
          pickupFabricWay: machineFormData.value.pickupFabricWay,
          shippingAddress: machineFormData.value.shippingAddress,
          serialNumber: formData.serialNumber,
          images: formData.images,
          styleType: machineFormData.value.styleType,
          styleTypeName: machineFormData.value.styleTypeName,
          serviceObject: machineFormData.value.serviceObject,
          serviceObjectId: machineFormData.value.serviceObjectId,
          // ...machineFormData.value,
          // ...formData,
        };
        await updateProductionDemand(data);

        ElMessage.success('操作成功');
        setTimeout(() => {
          router.back();
        }, 0);
      } catch (err) {
        console.log(err);
      }
    };

    const handleCancel = async () => {
      await ElMessageBox.confirm('确认放弃编辑返回上一页吗？', '提示', { type: 'warning' });
      router.back();
    };

    // 字典相关
    const {
      batchDictListMap,
    } = useDictionary([
      'pims_category',
      'plm_standard_size',
      'plm_quality_level',
    ]);
    // 款式品类
    const categoryTree = useOpsCascader('pims_category');
    // 标准尺码 true，过滤出已启用的
    const clothesSize = useClothesSize();
    // 品质等级
    const qualityLevel = computed(() => {
      return batchDictListMap.value?.plm_quality_level;
    });

    return {
      permissionConfig: usePermissionConfig(),
      mainFormRef,
      machineRef,
      formData,
      machineFormData,
      handleSubmit,
      handleCancel,
      isCreditCustomer,

      REGION_LIST,
      DEMAND_TASK_MACHINE_LIST,

      batchDictListMap,
      categoryTree,
      clothesSize,
      qualityLevel,

      rules: {
        regionId: [{ required: true, message: '请选择所属区域', trigger: ['change'] }],
        demandTaskType: [{ required: true, message: '请选择需求类型', trigger: ['change'] }],
        serialNumber: [{ required: true, message: '请输入飞书审批编号', trigger: ['change'] }],
        _images: [{ required: true, message: '请上传审批截图', trigger: ['change'] }],
      },
    };
  },
});
